import { useState } from 'react';
import {
  IconAlertTriangle,
  IconMailCheck,
  IconMailExclamation,
  IconMailForward,
} from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { z } from 'zod';
import {
  Affix,
  Alert,
  Box,
  Button,
  Center,
  Group,
  Modal,
  Paper,
  PinInput,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { OtpCountdown, useSecondsLeft } from '@/components/Auth/OtpCountdown';
import { VERIFY_EMAIL_OTP_WINDOW } from '@/constants';
import { useAuth } from '@/contexts/AuthContext';
import { useMenuContext } from '@/hooks/organizerHooks/useMenuContext';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useScreenLargerThan } from '@/hooks/useScreenLargerThan';
import {
  OtpWindow,
  otpWindowFrom,
  sendOtpByEmail,
  verifyTokenSentToEmail,
  VerifyTokenSentToEmail,
} from '@/services/api/auth/auth.service';
import { otpTokenSchema } from '@/types/user';

/** Liga o modal só onde a variável estiver "on" (por enquanto, apenas no ambiente local com o Mailpit). */
export const EMAIL_VERIFICATION_ENABLED = import.meta.env.VITE_EMAIL_VERIFICATION === 'on';

/** "Agora não" fecha o modal até fechar o navegador (no próximo acesso ele volta); o lembrete bege fica. */
const DISMISSED_KEY = 'emailVerificationDismissed';

const isDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
};

const errorMessage = (error: AxiosError, fallback: string) =>
  (error.response?.data as { message?: string } | undefined)?.message ?? fallback;

/** Primeiro nome em "Título" (os nomes são gravados em maiúsculas). */
const firstName = (name: string) => {
  const first = name.trim().split(/\s+/)[0] ?? '';
  return first.charAt(0).toLocaleUpperCase('pt-BR') + first.slice(1).toLocaleLowerCase('pt-BR');
};

/** Por que confirmar: o texto muda para quem organiza eventos. */
function Reasons({ isOrganizer }: { isOrganizer: boolean }) {
  return isOrganizer ? (
    <Text size="sm" c="dimmed" ta="center">
      É por este e-mail que você recebe os avisos de novas inscrições e pagamentos dos seus eventos,
      e é com ele que recupera o acesso ao painel se esquecer a senha. Confirmar leva menos de um
      minuto.
    </Text>
  ) : (
    <Text size="sm" c="dimmed" ta="center">
      É por este e-mail que você recebe a confirmação das suas inscrições, os comprovantes de
      pagamento e os avisos dos eventos, e é com ele que recupera a conta se esquecer a senha.
      Confirmar leva menos de um minuto.
    </Text>
  );
}

// Bege fixo nos dois temas: o lembrete precisa chamar atenção sem parecer erro
const BEIGE = { bg: '#f6ecd6', border: '#e4d1a6', text: '#4a3b17', accent: '#8a6a1f' };

/** Lembrete bege no canto da tela enquanto o e-mail não é confirmado (não é obrigatório confirmar). */
function ReminderBanner({
  isOrganizer,
  onConfirm,
}: {
  isOrganizer: boolean;
  onConfirm: () => void;
}) {
  const screenLargerThanXs = useScreenLargerThan('xs');
  return (
    // Canto inferior direito (à esquerda fica o "Sair" do menu do painel), com espaço à direita para
    // o botão "voltar ao topo"
    <Affix
      position={
        screenLargerThanXs ? { bottom: 20, right: 84 } : { bottom: 12, left: 12, right: 68 }
      }
    >
      <Paper
        role="status"
        radius="md"
        p="sm"
        shadow="md"
        maw={screenLargerThanXs ? 440 : undefined}
        style={{
          backgroundColor: BEIGE.bg,
          border: `1px solid ${BEIGE.border}`,
          color: BEIGE.text,
        }}
      >
        <Group wrap="nowrap" gap="sm" align="center">
          <IconMailExclamation size={28} color={BEIGE.accent} style={{ flexShrink: 0 }} />
          <Box style={{ minWidth: 0, flex: 1 }}>
            <Text fw={700} size="sm" c={BEIGE.text}>
              Confirme seu e-mail
            </Text>
            <Text size="xs" c={BEIGE.text} lh={1.35}>
              {isOrganizer
                ? 'Garante os avisos de inscrições, de pagamentos e o acesso ao painel.'
                : 'Garante seus comprovantes, os avisos dos eventos e o acesso à conta.'}
            </Text>
          </Box>
          <Button
            size="xs"
            radius="md"
            color="dark"
            variant="filled"
            onClick={onConfirm}
            style={{ flexShrink: 0 }}
          >
            Confirmar
          </Button>
        </Group>
      </Paper>
    </Affix>
  );
}

/**
 * Para quem entrou sem confirmar o e-mail: modal ao entrar (pode adiar com "Agora não") e um lembrete
 * bege em todas as telas, que reabre o modal. Confirmar não é obrigatório, mas é recomendado.
 * Dois passos no modal: enviar o código e digitar os 6 dígitos (contador, tentativas e reenvio).
 */
export function EmailVerificationReminder() {
  const { currentUser, setCurrentUser, saveAuth } = useAuth();
  const { getLocalStorage, saveLocalStorage, removeLocalStorage } = useLocalStorage();
  const screenLargerThanXs = useScreenLargerThan('xs');

  const [dismissed, setDismissed] = useState(isDismissed);
  // Prazo do código já enviado (sobrevive a recarregar a página)
  const [otpWindow, setOtpWindow] = useState<OtpWindow | null>(
    () => (getLocalStorage(VERIFY_EMAIL_OTP_WINDOW) as OtpWindow | undefined) ?? null
  );
  // Passo 2 (digitar o código) mesmo sem prazo salvo: o servidor disse que já há um código valendo
  const [codeSent, setCodeSent] = useState(false);
  const [otpCancelled, setOtpCancelled] = useState(false);
  const secondsLeft = useSecondsLeft(otpWindow?.deadline ?? null);

  const onCodeStep = codeSent || otpCancelled || otpWindow !== null;
  const otpExpired = otpCancelled || (otpWindow !== null && secondsLeft === 0);

  // Mensagem de organizador para quem tem evento próprio (qualquer conta organiza)
  const { isAdmin, ownsEvents } = useMenuContext();
  const isOrganizer = isAdmin || ownsEvents;
  const needsVerification =
    EMAIL_VERIFICATION_ENABLED && !!currentUser && currentUser.isEmailVerified === false;

  const form = useForm({
    initialValues: { otpToken: '' },
    validate: zodResolver(otpTokenSchema),
  });

  const sendMutation = useMutation({
    mutationFn: () => sendOtpByEmail(),
    onSuccess: (res) => {
      const nextWindow = otpWindowFrom(res);
      saveLocalStorage(VERIFY_EMAIL_OTP_WINDOW, nextWindow);
      setOtpWindow(nextWindow);
      setOtpCancelled(false);
      setCodeSent(true);
      form.reset();
    },
    onError: (error: AxiosError) => {
      const message = errorMessage(error, 'Não foi possível enviar o código. Tente novamente.');
      // 400: já existe um código valendo; segue para digitar, sem contador
      if (error.response?.status === 400) {
        removeLocalStorage(VERIFY_EMAIL_OTP_WINDOW);
        setOtpWindow(null);
        setOtpCancelled(false);
        setCodeSent(true);
        showNotification({ color: 'yellow', title: 'Seu código ainda vale', message });
        return;
      }
      showNotification({ color: 'red', title: 'Erro ao enviar o código', message });
    },
  });

  const verifyMutation = useMutation({
    mutationFn: (data: VerifyTokenSentToEmail) => verifyTokenSentToEmail(data),
    onSuccess: (tokens) => {
      saveAuth(tokens);
      removeLocalStorage(VERIFY_EMAIL_OTP_WINDOW);
      setCurrentUser((user) => (user ? { ...user, isEmailVerified: true } : user));
      showNotification({
        color: 'principal',
        title: 'E-mail confirmado',
        message: 'Tudo certo! Seu e-mail foi confirmado.',
      });
    },
    onError: (error: AxiosError) => {
      // 410: expirado ou cancelado; só resta pedir outro
      if (error.response?.status === 410) {
        setOtpCancelled(true);
      }
      form.setFieldValue('otpToken', '');
      showNotification({
        color: 'red',
        title: 'Código não aceito',
        message: errorMessage(error, 'Não foi possível verificar o código. Tente novamente.'),
      });
    },
  });

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // navegação privada: o modal só não fica dispensado ao recarregar
    }
    setDismissed(true);
  };

  const handleVerify = ({ otpToken }: z.infer<typeof otpTokenSchema>) => {
    if (currentUser && !otpExpired) {
      verifyMutation.mutate({ otpToken, email: currentUser.email });
    }
  };

  const reopen = () => {
    try {
      sessionStorage.removeItem(DISMISSED_KEY);
    } catch {
      // sem sessionStorage: só reabre
    }
    setDismissed(false);
  };

  if (!needsVerification || !currentUser) {
    return null;
  }

  if (dismissed) {
    return <ReminderBanner isOrganizer={isOrganizer} onConfirm={reopen} />;
  }

  return (
    <Modal
      opened
      onClose={dismiss}
      centered
      radius="md"
      size="md"
      padding="xl"
      closeOnClickOutside={false}
      withCloseButton={false}
      title={null}
    >
      <Stack gap="md">
        <Center>
          <ThemeIcon size={56} radius="xl" color="principal" variant="light">
            <IconMailCheck size={30} />
          </ThemeIcon>
        </Center>
        <Title order={screenLargerThanXs ? 2 : 3} ta="center">
          Confirme seu e-mail
        </Title>

        {!onCodeStep ? (
          <>
            <Text ta="center">
              Olá, {firstName(currentUser.name)}! Vamos enviar um código de 6 dígitos para{' '}
              <b style={{ wordBreak: 'break-all' }}>{currentUser.email}</b>.
            </Text>
            <Reasons isOrganizer={isOrganizer} />
            <Button
              fullWidth
              size="md"
              color="principal.9"
              leftSection={<IconMailForward size="1.2rem" />}
              loading={sendMutation.isPending}
              onClick={() => sendMutation.mutate()}
            >
              Enviar código
            </Button>
          </>
        ) : (
          <>
            <Text ta="center" c="dimmed">
              Digite o código de 6 dígitos que enviamos para{' '}
              <b style={{ wordBreak: 'break-all' }}>{currentUser.email}</b>. Ele também aparece no
              assunto do e-mail.
            </Text>

            {otpExpired ? (
              <Alert color="red" variant="light" icon={<IconAlertTriangle size={20} />}>
                {otpCancelled
                  ? 'Este código foi cancelado. Peça um novo código para continuar.'
                  : 'O código expirou. Peça um novo código para continuar.'}
              </Alert>
            ) : (
              otpWindow && (
                <OtpCountdown secondsLeft={secondsLeft} totalSeconds={otpWindow.totalSeconds} />
              )
            )}

            <form onSubmit={form.onSubmit(handleVerify)}>
              <Stack>
                <Center>
                  <PinInput
                    length={6}
                    size={screenLargerThanXs ? 'lg' : 'md'}
                    gap={screenLargerThanXs ? 'sm' : 6}
                    type="number"
                    oneTimeCode
                    disabled={otpExpired}
                    aria-label="Código de 6 dígitos"
                    {...form.getInputProps('otpToken')}
                  />
                </Center>

                {otpExpired ? (
                  <Button
                    fullWidth
                    size="md"
                    color="principal.9"
                    leftSection={<IconMailForward size="1.2rem" />}
                    loading={sendMutation.isPending}
                    onClick={() => sendMutation.mutate()}
                  >
                    Enviar novo código
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    size="md"
                    type="submit"
                    color="principal.9"
                    loading={verifyMutation.isPending}
                  >
                    Confirmar e-mail
                  </Button>
                )}
              </Stack>
            </form>

            <Text size="xs" c="dimmed" ta="center">
              Não chegou? Confira a caixa de spam. Quando o contador zerar, você pode pedir outro
              código.
            </Text>
          </>
        )}

        <Group justify="center">
          <Button variant="subtle" color="gray" size="xs" onClick={dismiss}>
            Agora não
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
